import { useEffect, useState } from "react";
import {
  EMPTY_INDEXES,
  clearIndexCache,
  getCachedIndexes,
  loadIndexes,
  subscribeIndexes,
  type IndexesPayload,
} from "@/services/configApi";

export interface IndexesState {
  indexes: IndexesPayload;
  isLoading: boolean;
  error: string | null;
  /** True while the data shown came from the persisted cache and no network copy has landed yet. */
  fromCache: boolean;
}

/**
 * Load the canonical dimension indexes from the backend (DB kernels).
 *
 * MR14: DB kernels are the runtime source of truth — there is no bundled JSON
 * fallback. The first render is served synchronously from the persisted cache
 * (written by an earlier session or by `prefetchIndexes()` at app start), and a
 * background revalidation swaps in the live copy when it differs. Consumers get
 * `EMPTY_INDEXES` with `isLoading: true` only on a genuinely cold start.
 */
export function useIndexDimensions(): IndexesState {
  const [state, setState] = useState<IndexesState>(() => {
    const cached = getCachedIndexes();
    return {
      indexes: cached ?? EMPTY_INDEXES,
      isLoading: !cached,
      error: null,
      fromCache: !!cached,
    };
  });

  useEffect(() => {
    let cancelled = false;

    // Picks up the app-start revalidation (and any other consumer's fetch) so
    // every mounted hook converges on one payload without refetching.
    const unsubscribe = subscribeIndexes((indexes) => {
      if (!cancelled) setState({ indexes, isLoading: false, error: null, fromCache: false });
    });

    loadIndexes()
      .then(({ indexes, source }) => {
        if (cancelled) return;
        setState((prev) =>
          prev.indexes === indexes && !prev.isLoading && prev.fromCache === (source === "cache")
            ? prev
            : { indexes, isLoading: false, error: null, fromCache: source === "cache" },
        );
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[useIndexDimensions] Failed to load dimension indexes:", e);
        setState((prev) => ({ ...prev, isLoading: false, error: (e as Error).message }));
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return state;
}

/** Maps "<bd>.<bdsub>" → the KD extension's value labels, e.g.
 *  { "155.9": { "1": "post publishing", "2": "profile scraping", ... } }. */
export type KdExtensionValuesMap = Record<string, Record<string, string>>;

interface KdDimensionDef {
  extension_of?: string;
  values?: Record<string, string>;
}

/**
 * Labels for the THIRD segment of a classification code (155.9.1 → "post publishing").
 *
 * Only per-BDsub category dims carry `extension_of` and may appear as a third
 * segment; meta dims (900/901/910-940/999) are tags, never part of the code — see
 * `classification_rules.extension_rule` in kernel_index_dimensions.
 */
export function useKdExtensionValues(): KdExtensionValuesMap {
  const { indexes } = useIndexDimensions();
  const [map, setMap] = useState<KdExtensionValuesMap>({});

  useEffect(() => {
    const dims = (indexes.kernel_index_dimensions as { dimensions?: Record<string, KdDimensionDef> })
      ?.dimensions;
    if (!dims) return;
    const next: KdExtensionValuesMap = {};
    for (const def of Object.values(dims)) {
      const match = def?.extension_of?.match(/^BD(\d+)\.(\d+)$/);
      if (match && def.values) next[`${match[1]}.${match[2]}`] = def.values;
    }
    setMap(next);
  }, [indexes]);

  return map;
}

export { clearIndexCache as clearIndexDimensionsCache };
